import { Suspense, lazy } from 'react';
import type { ComponentType, JSX, LazyExoticComponent } from 'react';
import type { PaymentIntent } from '../types/api';
import type { CheckoutSession } from '../session/useCheckoutSession';
import type { Locale } from '../i18n';
import { ErrorMessage } from '../components/ErrorMessage';
import { HandoffScreen } from '../components/HandoffScreen';
import { widgetText } from '../components/copy';

export interface GatewayFlowProps {
  intent: PaymentIntent;
  session: CheckoutSession;
  locale: Locale;
  onCancel(): void;
  /**
   * Arranca el flujo sin pedir un segundo click. Se usa en MODO PAGINA, donde el comprador ya pulso
   * «Ir a pagar» en el selector: repetir el gesto no aporta nada. Embebido va a `false` a proposito,
   * porque la navegacion de la ventana superior desde un iframe exige activacion del usuario y una
   * redireccion lanzada desde un efecto la habria perdido.
   */
  autoStart?: boolean | undefined;
}

/** Props que reciben todas las estrategias: identicas a las del despachador. */
interface StrategyProps {
  intent: PaymentIntent;
  session: CheckoutSession;
  autoStart?: boolean | undefined;
  locale: Locale;
  onCancel(): void;
}

type StrategyComponent = LazyExoticComponent<ComponentType<StrategyProps>>;

// import() dinamico: cada estrategia viaja en su propio chunk y solo se descarga cuando
// la pasarela del intent la necesita (plan 7.3.4). Se mapea el export nombrado a `default`
// porque React.lazy lo exige y el monorepo prohibe default exports.
const STRATEGIES: Record<string, StrategyComponent> = {
  webpay: lazy(async () => ({ default: (await import('./WebpayRedirectFlow')).WebpayRedirectFlow })),
  oneclick: lazy(async () => ({
    default: (await import('./OneclickInscriptionFlow')).OneclickInscriptionFlow,
  })),
  mercadopago: lazy(async () => ({
    default: (await import('./MercadoPagoBricksFlow')).MercadoPagoBricksFlow,
  })),
  sandbox: lazy(async () => ({ default: (await import('./SandboxFlow')).SandboxFlow })),
  // RutPay cobra de DOS formas y las dos viven en la misma estrategia: si la respuesta trae codigo
  // QR se pinta y se sondea; si trae URL, se delega en ConfirmOnReturnFlow. Quien elige es el
  // ADAPTADOR, por el User-Agent, no el widget.
  rutpay: lazy(async () => ({ default: (await import('./RutPayFlow')).RutPayFlow })),
  // Global Getnet y CompraAqui comparten estrategia: las dos son redireccion pura y en las dos el
  // desenlace lo trae el webhook, no el retorno del comprador. El POR QUE de que sea una sola
  // esta en WebhookRedirectFlow.
  globalgetnet: lazy(async () => ({
    default: (await import('./WebhookRedirectFlow')).WebhookRedirectFlow,
  })),
  compraaqui: lazy(async () => ({
    default: (await import('./WebhookRedirectFlow')).WebhookRedirectFlow,
  })),
  // Getnet --la de checkout.getnet.cl-- NO comparte la de arriba, y no es un descuido: ella si
  // publica consulta de estado, asi que su confirm decide y el retorno del comprador puede cerrar el
  // cobro. Reutilizar WebhookRedirectFlow seria quedarse esperando un webhook que ya no hace falta.
  getnet: lazy(async () => ({
    default: (await import('./ConfirmOnReturnFlow')).ConfirmOnReturnFlow,
  })),
  // MACH comparte la de getnet, y esta eleccion cambio dos veces en dos dias.
  //
  // Nacio en `WebhookRedirectFlow` porque no se sabia si su checkout devolvia al comprador. El
  // 2026-08-25 se midio que no volvia y se movio aqui: si no vuelve solo, lo unico que puede cerrar
  // el cobro cuando vuelve POR SU CUENTA --boton atras, el comercio reabriendo la URL, un
  // remontaje-- es PREGUNTAR, y `WebhookRedirectFlow` se quedaria esperando un webhook de CONFIRMED
  // que MACH no publica.
  //
  // El 2026-08-26 resulto que SI vuelve: `POST /payments` tiene un campo `return_url` que no
  // estabamos mandando (laguna L-7 de Docs/CONTRATO-MACH.md, cerrada al reves de como se cerro un
  // dia antes). Eso no mueve la estrategia --la confirma-- porque el retorno de MACH no trae
  // parametros del desenlace, exactamente como el de getnet: al volver hay que consultar.
  mach: lazy(async () => ({
    default: (await import('./ConfirmOnReturnFlow')).ConfirmOnReturnFlow,
  })),
  // Amipass comparte la de getnet y mach, y aqui el motivo es el mas fuerte de los tres: es la
  // UNICA pasarela del catalogo que NO NOTIFICA NADA (V025 y CONTRATO-AMIPASS 5). No declara
  // NATIVE_WEBHOOKS porque no tiene ningun canal de aviso, asi que `WebhookRedirectFlow` se
  // quedaria esperando para siempre un webhook que no existe.
  //
  // Lo unico que llega solo es el POST del navegador a urlExito/urlFracaso, sin firma y con campos
  // sin documentar (laguna L-5): sirve para saber que el comprador termino, no para saber COMO
  // termino. Por eso el desenlace lo decide `toValid`, que es la consulta autoritativa, y eso es
  // exactamente lo que hace el segundo confirm de esta estrategia.
  amipass: lazy(async () => ({
    default: (await import('./ConfirmOnReturnFlow')).ConfirmOnReturnFlow,
  })),
  // BCI es la UNICA que no redirige: su Hosted Checkout se monta EMBEBIDO cargando el
  // `checkout.min.js` del host de Mastercard Gateway. Por eso su adaptador devuelve `IframeAction`
  // y no `RedirectAction`, y por eso no puede compartir ninguna de las de arriba: no hay a donde
  // navegar. La mas parecida es la de MercadoPago --script externo + montaje-- pero el SDK y los
  // callbacks no se parecen en nada.
  bci: lazy(async () => ({
    default: (await import('./BciHostedCheckoutFlow')).BciHostedCheckoutFlow,
  })),
  // Santander tampoco redirige, y de una tercera forma: se le ENVIA UN FORMULARIO a una ventana
  // emergente (`PopupFormAction`), que ademas se cierra sola sin avisar a esta pestaña. Por eso el
  // desenlace llega siempre por el sondeo. El POR QUE completo esta en SantanderPopupFlow.
  santander: lazy(async () => ({
    default: (await import('./SantanderPopupFlow')).SantanderPopupFlow,
  })),
};

/**
 * Pasarelas que este widget sabe RENDERIZAR, derivadas de las claves del propio mapa `STRATEGIES`.
 *
 * <p>Se derivan y no se escriben porque una lista paralela se desincroniza en cuanto alguien anade o
 * quita una estrategia, y el precio de la desincronizacion lo paga el comprador: el selector le
 * ofreceria un medio de pago que al pulsarlo cae en la rama `gateway_unavailable` de aqui abajo.
 * Quien anada una estrategia al mapa la pone en el selector con el mismo gesto.
 *
 * <p>Lo consume un solo sitio: `renderableMethods` en `session/uiState`, que es donde vive la regla de
 * que se ofrece y de donde beben tanto `MethodSelector` (que filas pinta) como el mapeo de estados
 * (si hay algo que elegir). Que el consumidor sea uno es lo que impide que esas dos cuentas se
 * separen; el POR QUE esta documentado alli.
 */
export const KNOWN_GATEWAY_IDS: ReadonlySet<string> = new Set<string>(Object.keys(STRATEGIES));

/**
 * Despacha la estrategia de pasarela por `checkout.gateway_id`. Un identificador que este
 * widget no conoce (pasarela nueva en el backend, build viejo en cache del navegador)
 * degrada a un error legible en vez de romper el checkout.
 */
export function GatewayFlow({
  intent,
  session,
  locale,
  onCancel,
  autoStart,
}: GatewayFlowProps): JSX.Element {
  const gatewayId = intent.checkout.gateway_id;
  const Strategy = STRATEGIES[gatewayId];

  if (Strategy === undefined) {
    return (
      <ErrorMessage
        code="gateway_unavailable"
        message={widgetText(locale, 'gateway.unavailable')}
        locale={locale}
      />
    );
  }

  return (
    <div data-gateway={gatewayId}>
      {/*
        La espera del chunk usa la MISMA pantalla que la carga de la sesion y que el traspaso de
        las estrategias. Antes era una linea de estado suelta, con otra altura y otra forma, asi
        que la descarga del chunk se veia como una pantalla mas del camino (ver HandoffScreen).
      */}
      <Suspense fallback={<HandoffScreen message={widgetText(locale, 'gateway.loading')} />}>
        <Strategy
          intent={intent}
          session={session}
          locale={locale}
          onCancel={onCancel}
          autoStart={autoStart}
        />
      </Suspense>
    </div>
  );
}
